/**
 * Root providers + routing. The route table and nav are assembled from the feature
 * registry (docs/adr/002-modular-feature-registry.md) filtered by the runtime
 * feature flags + caller roles served by `/meta/config`. Router basename comes from
 * Vite's BASE_URL (the dashboard mount) so links resolve under the nginx URL root.
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense } from "react";
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";

import { AuthProvider, useAuth } from "@/auth/AuthContext";
import ForgotPasswordPage from "@/auth/ForgotPasswordPage";
import LoginPage from "@/auth/LoginPage";
import { RequireAuth } from "@/auth/RequireAuth";
import ResetPasswordPage from "@/auth/ResetPasswordPage";
import { AppShell } from "@/components/AppShell";
import { ChartSkeleton } from "@/components/LoadingSkeleton";
import PublicBillPage from "@/features/billing/PublicBillPage";
import { enabledFeatures } from "@/features/registry";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { ScopeProvider } from "@/scope/ScopeContext";
import { ThemeProvider } from "@/theme/ThemeProvider";

// One client for the whole app. Defaults tuned for a self-hosted ops backend:
// keep data a little stale rather than hammering the API, retry sparingly.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 30 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Vite injects the base path; strip the trailing slash for React Router.
const basename = import.meta.env.BASE_URL.replace(/\/$/, "");

function FullPageLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg text-sm text-fg-subtle">
      …
    </div>
  );
}

function AppRoutes() {
  const { loading, features, user, appName } = useAuth();
  // Above the early return so the hook order stays fixed, and outside AppShell
  // so the login screen gets the right tab title too.
  useDocumentTitle(appName);
  if (loading) return <FullPageLoading />;

  const feats = enabledFeatures(features, user);
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Public, unauthenticated — the signed link a customer receives to view
          their own bill. Matches GET /billing/b/{token}, which needs no auth. */}
      <Route path="/bill/:token" element={<PublicBillPage />} />
      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          {feats.flatMap((f) => {
            const wrap = (El: (typeof f)["element"]) => (
              <Suspense fallback={<ChartSkeleton />}>
                <El />
              </Suspense>
            );
            const routes = [
              f.index ? (
                <Route key={f.id} index element={wrap(f.element)} />
              ) : (
                <Route key={f.id} path={f.path.replace(/^\//, "")} element={wrap(f.element)} />
              ),
              ...(f.subRoutes ?? []).map((sub) => (
                <Route key={sub.path} path={sub.path.replace(/^\//, "")} element={wrap(sub.element)} />
              )),
            ];
            return routes;
          })}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router basename={basename || "/"}>
          <AuthProvider>
            <ScopeProvider>
              <AppRoutes />
            </ScopeProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
